import * as THREE from 'three';
import gsap from 'gsap';
import { createWebGLRenderer, hasWebGL } from './webgl.js';

const vertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = /* glsl */ `
precision highp float;

uniform sampler2D u_texture;
uniform float u_imageAspectRatio;
uniform float u_aspectRatio;
uniform float u_opacity;
uniform float u_hover;
uniform float u_zoomLevel;
uniform float u_rgbShift;
uniform float u_pixelDisplace;
uniform float u_borderRadius;
uniform vec3  u_tint;
uniform float u_tintStrength;
uniform vec2  u_resolution;
varying vec2 vUv;

float exponentialInOut(float t) {
  return t == 0.0 || t == 1.0
    ? t
    : t < 0.5
      ? +0.5 * pow(2.0, (20.0 * t) - 10.0)
      : -0.5 * pow(2.0, 10.0 - (t * 20.0)) + 1.0;
}

float roundedBoxSDF(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
}

void main() {
  vec2 uv = vUv;

  vec2 pixelCoord = vUv * u_resolution;
  vec2 center = u_resolution * 0.5;
  vec2 halfSize = u_resolution * 0.5;
  float dist = roundedBoxSDF(pixelCoord - center, halfSize, u_borderRadius);

  if (dist > 0.0) {
    discard;
  }

  float alpha = 1.0 - smoothstep(-1.0, 1.0, dist);

  float u = u_imageAspectRatio / u_aspectRatio;
  if (u_imageAspectRatio > u_aspectRatio) {
    u = 1.0 / u;
  }

  uv.y *= u;
  uv.y -= (u) / 2.0 - 0.5;

  float hoverLevel = exponentialInOut(min(1.0, (distance(vec2(0.5), uv) * u_hover) + u_hover));
  uv *= 1.0 - u_zoomLevel * hoverLevel;
  uv += u_zoomLevel / 2.0 * hoverLevel;
  uv = clamp(uv, 0.0, 1.0);
  vec4 color = texture2D(u_texture, uv);

  if (hoverLevel > 0.0) {
    float peak = 1.0 - abs(hoverLevel - 0.5) * 2.0;
    uv.y += color.r * peak * u_pixelDisplace;
    color = texture2D(u_texture, uv);
    color.r = texture2D(u_texture, uv + peak * u_rgbShift).r;
    color.g = texture2D(u_texture, uv - peak * u_rgbShift).g;
    color.rgb = mix(color.rgb, color.rgb * u_tint, peak * u_tintStrength);
  }

  gl_FragColor = vec4(color.rgb, color.a * u_opacity * alpha);
}
`;

function hexToRgb(hex) {
  const n = hex.replace('#', '');
  const r = parseInt(n.substring(0, 2), 16) / 255;
  const g = parseInt(n.substring(2, 4), 16) / 255;
  const b = parseInt(n.substring(4, 6), 16) / 255;
  return [r, g, b];
}

export function initChromaCard(target, options = {}) {
  if (!target) return null;

  const img = target.querySelector('img');
  // Without WebGL 2 the portrait stays a plain <img>, as for reduced motion.
  if (!img || !hasWebGL()) return null;

  const imageSrc = options.imageSrc ?? img.getAttribute('src');

  const settings = {
    cardWidth: 5,
    cardHeight: 6,
    zoomLevel: 0.28,
    rgbShiftAmount: 0.018,
    pixelDisplaceAmount: 0.09,
    hoverDuration: 2.4,
    rotationIntensity: 0.22,
    scaleIntensity: 0.08,
    positionIntensity: 0.35,
    interactionDuration: 0.5,
    opacity: 1,
    cameraFov: 50,
    cameraZ: 7,
    borderRadius: 6,
    tint: '#7A1F2B',
    tintStrength: 0.18,
    ...options,
  };

  // Start the renderer before touching the DOM: if it won't, the <img> has to
  // still be showing.
  const canvas = document.createElement('canvas');
  canvas.className = 'chroma-card-canvas';
  const renderer = createWebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
  });
  if (!renderer) return null;

  // Hide the original <img> — keep it for SEO/alt but don't render visually.
  img.style.opacity = '0';
  img.style.visibility = 'hidden';

  target.classList.add('chroma-card-host');
  target.appendChild(canvas);

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.NoToneMapping;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(settings.cameraFov, 1, 0.1, 100);
  camera.position.set(0, 0, settings.cameraZ);

  const loader = new THREE.TextureLoader();
  loader.crossOrigin = 'anonymous';

  // The photo has to come out of the card exactly as the browser would paint
  // the <img>. Declaring the texture as sRGB makes the GPU decode it to linear
  // light on sampling, and this custom ShaderMaterial never re-encodes on
  // output (no `colorspace_fragment` chunk), so the linear values were shown
  // as if they were sRGB — the portrait rendered ~40% too dark (avatar.jpg
  // mean luma 69 → 40). Keep the texture raw (NoColorSpace) so the bytes pass
  // straight through, the same way lib/gridDistortion.js already does; the
  // hover effects are plain texture lookups and need no linear-light math.
  const texture = loader.load(imageSrc, (tex) => {
    const { naturalWidth, naturalHeight } = tex.image;
    if (naturalWidth && naturalHeight) {
      material.uniforms.u_imageAspectRatio.value = naturalWidth / naturalHeight;
    }
  });
  texture.colorSpace = THREE.NoColorSpace;

  const [tr, tg, tb] = hexToRgb(settings.tint);

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      u_texture: { value: texture },
      u_imageAspectRatio: {
        value: img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0.8,
      },
      u_aspectRatio: { value: settings.cardWidth / settings.cardHeight },
      u_opacity: { value: settings.opacity },
      u_hover: { value: 0 },
      u_zoomLevel: { value: settings.zoomLevel },
      u_rgbShift: { value: settings.rgbShiftAmount },
      u_pixelDisplace: { value: settings.pixelDisplaceAmount },
      u_borderRadius: { value: settings.borderRadius },
      u_tint: { value: new THREE.Vector3(tr, tg, tb) },
      u_tintStrength: { value: settings.tintStrength },
      u_resolution: { value: new THREE.Vector2(500, 700) },
    },
    transparent: true,
  });

  const geometry = new THREE.PlaneGeometry(settings.cardWidth, settings.cardHeight);
  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hoverCapable = window.matchMedia('(hover: hover)').matches;

  function resize() {
    const rect = target.getBoundingClientRect();
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    renderer.setSize(w, h, false);
    material.uniforms.u_resolution.value.set(
      w * renderer.getPixelRatio(),
      h * renderer.getPixelRatio()
    );
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  resize();

  const ro = new ResizeObserver(resize);
  ro.observe(target);

  function onPointerMove(e) {
    if (prefersReducedMotion) return;
    const rect = canvas.getBoundingClientRect();
    // The host can take the pointer while the canvas has no area; dividing by
    // zero would leave NaN in the tweened position, rotation and scale.
    if (!rect.width || !rect.height) return;
    mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);
    const hits = raycaster.intersectObject(mesh);

    gsap.to(material.uniforms.u_hover, {
      value: hits.length > 0 ? 1 : 0,
      duration: settings.hoverDuration,
      overwrite: 'auto',
    });

    gsap.to(mesh.scale, {
      x: 1 - mouse.y * settings.scaleIntensity,
      y: 1 - mouse.y * settings.scaleIntensity,
      duration: settings.interactionDuration,
      overwrite: 'auto',
    });

    gsap.to(mesh.position, {
      x: mouse.x * settings.positionIntensity,
      duration: settings.interactionDuration,
      overwrite: 'auto',
    });

    gsap.to(mesh.rotation, {
      x: -mouse.y * (Math.PI / 3) * settings.rotationIntensity,
      y: mouse.x * (Math.PI / 3) * settings.rotationIntensity,
      duration: settings.interactionDuration,
      overwrite: 'auto',
    });
  }

  function onPointerLeave() {
    gsap.to(material.uniforms.u_hover, {
      value: 0,
      duration: settings.hoverDuration,
      overwrite: 'auto',
    });
    gsap.to(mesh.position, { x: 0, duration: settings.interactionDuration, overwrite: 'auto' });
    gsap.to(mesh.rotation, {
      x: 0,
      y: 0,
      duration: settings.interactionDuration,
      overwrite: 'auto',
    });
    gsap.to(mesh.scale, { x: 1, y: 1, duration: settings.interactionDuration, overwrite: 'auto' });
  }

  if (hoverCapable) {
    target.addEventListener('pointermove', onPointerMove);
    target.addEventListener('pointerleave', onPointerLeave);
  }

  let raf;
  function render() {
    renderer.render(scene, camera);
    raf = requestAnimationFrame(render);
  }
  render();

  return {
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerleave', onPointerLeave);
      geometry.dispose();
      material.dispose();
      texture.dispose();
      renderer.dispose();
      canvas.remove();
    },
  };
}
